import { useLocalObservable } from 'mobx-react-lite';
import { ElementType } from 'react';

import { IcLogin24Icon, IcUserCircle24Icon } from '@gem/icons';
import { useTranslation } from '@gem/modules/i18n';

import { storage } from 'APP/packages/storage';
import { IAuthorizationConfig } from 'APP/packages/storage/adapters/authorizationConfig';

import { ILoginViewProps } from '../../Login.types';
import { LoginEmailStep } from '../LoginEmailStep/LoginEmailStep';

export interface ILoginWithAuthorizationConfigProps extends ILoginViewProps {}

interface ILoginWithAuthorizationConfigPresenterParams extends ILoginWithAuthorizationConfigProps {}

type TLoginWithAuthorizationConfigListItem = {
  title: string;
  subtitle?: string;
  key: string;
  Icon: ElementType;
  onClick(): void | Promise<void>;
};

interface ILoginWithAuthorizationConfigPresenter {
  isLoading: boolean;
  authorizationConfig: IAuthorizationConfig | null;
  listItems: TLoginWithAuthorizationConfigListItem[];
  onAuthorizationConfigClick(): Promise<void>;
  onAddAnotherUserClick(): void;
}

export function useLoginWithAuthorizationConfigPresenter(
  params: ILoginWithAuthorizationConfigPresenterParams
): ILoginWithAuthorizationConfigPresenter {
  const { loginPresenter } = params;

  const { t } = useTranslation();

  const presenter = useLocalObservable(() => ({
    isLoading: false,

    get authorizationConfig(): IAuthorizationConfig | null {
      return storage.authorizationConfig.get();
    },

    get listItems(): TLoginWithAuthorizationConfigListItem[] {
      return [
        {
          key: 'authorization-config',
          title: presenter.authorizationConfig?.email || '',
          subtitle: presenter.authorizationConfig?.alias || '',
          onClick: presenter.onAuthorizationConfigClick,
          Icon: IcUserCircle24Icon,
        },
        {
          key: 'another-user',
          title: t('auth_sign_in_add_another_user'),
          onClick: presenter.onAddAnotherUserClick,
          Icon: IcLogin24Icon,
        },
      ];
    },

    async onAuthorizationConfigClick(): Promise<void> {
      if (!presenter.authorizationConfig) {
        return;
      }

      presenter.isLoading = true;

      loginPresenter.setEmail(presenter.authorizationConfig.email);
      storage.tenantRouting.update({
        bffUrl: presenter.authorizationConfig.baseUrl,
      });

      await loginPresenter.onAuthorize();
      presenter.isLoading = false;
    },

    onAddAnotherUserClick(): void {
      loginPresenter.resetErrors();
      loginPresenter.setEmail('');
      loginPresenter.openView(LoginEmailStep);
    },
  }));
  return presenter;
}
